import "./filtered-search.scss";

import { autoinject } from "aurelia-dependency-injection";
import { bindable } from "aurelia-templating";
import { UsersService } from "../../../services/users-service";
import { BindingEngine, bindingMode, Disposable, observable } from "aurelia-binding";
import nameof from "../../../common/nameof";
import { IGetUserFiltersResult } from "../../../interfaces/i-user";
import { deepAssign, deepClone, hasObjectChange } from "../../../common/utilities/object-extensions";
import {
    validateTrigger,
    ValidationController,
    ValidationControllerFactory,
    ValidationRules
} from "aurelia-validation";
import { ToastrService } from "../../../services/toastr-service";

@autoinject
export class FilteredSearch {
    /**
     * The filters that will be sent to the search
     */
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public searchFilters: any;
    /**
     * The selected filters options, with the format needed for the input components (e.g. ITypeaheadOptions[]'s)
     */
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedOptions: any;
    /**
     * The default search filter values
     */
    @bindable({ defaultBindingMode: bindingMode.toView })
    public defaultFilters: any;
    /**
     * The search filter values that may be passed in from the params
     */
    @bindable({ defaultBindingMode: bindingMode.toView })
    public parameterFilters: any;
    /**
     * The http route that the search uses
     */
    @bindable({ defaultBindingMode: bindingMode.toView })
    public source: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public search: () => Promise<void>;
    /**
     * Get the search filters based on the selection options
     */
    @bindable({ defaultBindingMode: bindingMode.toView })
    public getSelectedOptionsFromFilters: () => Promise<any>;
    /**
     * Get the selected filters options based on the updated search filters
     */
    @bindable({ defaultBindingMode: bindingMode.toView })
    public getFiltersFromSelected: () => Promise<any>;
    @observable({
        changeHandler: nameof<FilteredSearch>("selectedFilterChanged")
    })
    public selectedFilter: IGetUserFiltersResult;
    private _subscriptions: Disposable[] = [];
    private _bindingEngine: BindingEngine;
    private _userService: UsersService;
    private _toastrService: ToastrService;
    private _validationController: ValidationController;
    private _showDropdownListener: () => void;
    private _hideDropdownListener: (evt: MouseEvent) => void;
    private _resetId = "RESET";
    private _selectedOptionsCache: any;
    public isLoading: boolean = false;
    public searchInProgress: boolean = false;
    public saveInProgress: boolean = false;
    public savedFilters: IGetUserFiltersResult[] = [];
    public updatedName: string = "";
    public hasUpdatedName: boolean = false;
    public showClass: string;
    public dropdownIconRef: Element;
    public hasPendingChanges: boolean = false;

    public constructor(
        bindingEngine: BindingEngine,
        userService: UsersService,
        toastrService: ToastrService,
        validationFactory: ValidationControllerFactory
    ) {
        this._bindingEngine = bindingEngine;
        this._userService = userService;
        this._toastrService = toastrService;
        this._validationController = validationFactory.createForCurrentScope();
        this._validationController.validateTrigger = validateTrigger.changeOrBlur;
    }

    public async attached() {
        if (!!this.source) {
            this.isLoading = true;
            try {
                this.savedFilters =
                    (await this._userService.getUserFilters({
                        source: this.source
                    })) ?? [];
                if (!this.parameterFilters && this.savedFilters.length > 0) {
                    this.selectedFilter = this.savedFilters[0];
                } else {
                    this.selectedFilter = null;
                    this.runSearch(true);
                }
                this.savedFilters.unshift({
                    id: this._resetId,
                    name: "Reset Filters"
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Error Loading Saved Filters`,
                    message: `There was a problem while loading saved filters. Please reload the page to try again.`
                });
            } finally {
                this.isLoading = false;
            }
        }

        this._showDropdownListener = () => this.toggleDropdown(false);
        this._hideDropdownListener = (evt: MouseEvent) => this.toggleDropdown(true, evt);
        this.dropdownIconRef.addEventListener("click", this._showDropdownListener);
        document?.addEventListener("click", this._hideDropdownListener);

        this.initValidations();
        this.initObserver();
    }

    private initValidations() {
        ValidationRules.ensure((x: FilteredSearch) => x.updatedName)
            .required()
            .withMessage("Please name the new filter to save")
            .on(this);
    }

    private initObserver() {
        Object.keys(this.selectedOptions).forEach((prop) => {
            let field = this.selectedOptions[prop];
            let isArray = Array.isArray(field);
            let observer = isArray
                ? this._bindingEngine.collectionObserver(field)
                : this._bindingEngine.propertyObserver(this.searchFilters, prop);
            this._subscriptions.push(observer.subscribe(() => this.markSelectedChange()));
        });
    }

    public markSelectedChange() {
        if (!this.isLoading) {
            if (hasObjectChange(this._selectedOptionsCache, this.selectedOptions)) {
                this.hasPendingChanges = true;
                this._selectedOptionsCache = deepClone(this.selectedOptions);
            }
        }
    }

    public nameChanged() {
        // If this is a new filter, than any actual name works
        // otherwise a selected filter needs a non empty or existing name to count as a change
        this.hasUpdatedName =
            this.updatedName != "" && (!this.selectedFilter || this.selectedFilter.name !== this.updatedName);
    }

    private updateAllFilters(updatedAppliedFilters: any, resetSelectedCache: boolean = false) {
        deepAssign(this.searchFilters, updatedAppliedFilters);

        let updatedSelectedOptions = this.getSelectedOptionsFromFilters();
        // If this updated should not trigger pendingChanges, e.g. reset, then we should override the cache before the options
        if (resetSelectedCache) {
            this._selectedOptionsCache = deepClone(updatedSelectedOptions);
        }
        deepAssign(this.selectedOptions, updatedSelectedOptions);

        this.hasPendingChanges = false;
    }

    public getItemFont(item: IGetUserFiltersResult) {
        return item.id == this._resetId
            ? "font-italic font-weight-bold justify-content-center"
            : "justify-content-between";
    }

    public filterSelected(filter: IGetUserFiltersResult) {
        this.selectedFilter = filter;
    }

    public async selectedFilterChanged() {
        this.hasUpdatedName = false;
        if (!this.selectedFilter) {
            this.updatedName = "";
            return;
        }
        if (this.selectedFilter.id == this._resetId) {
            this.updateAllFilters(this.defaultFilters, true);
            await this.runSearch(true);
            this.selectedFilter = null;
            return;
        }

        this.updatedName = this.selectedFilter.name;
        // NOTE: We are not caching results for future selections, as the get call is also marking who the last usage is, which would have to be done separate call anyway
        let filterData = await this._userService.getUserFilter(this.selectedFilter.id);
        await this.updateAllFilters(deepClone(filterData));
        await this.runSearch(true);
    }

    public toggleDropdown(hide: boolean, evt?: MouseEvent) {
        if (!!hide && this.dropdownIconRef.contains(evt.target as Element)) {
            return;
        }
        this.showClass = !this.showClass && hide !== true ? "show" : "";
    }

    public async runSearch(isAutoSearch?: boolean) {
        this.searchInProgress = true;
        if (!isAutoSearch) {
            // Update search filters from what has been selected by user
            let updatedSearchFilters = this.getFiltersFromSelected();
            deepAssign(this.searchFilters, updatedSearchFilters);
            if (this.hasPendingChanges) {
                this.selectedFilter = null;
            }
        }
        this.hasPendingChanges = false;
        await this.search();
        this.searchInProgress = false;
    }

    public async saveFilter() {
        this.saveInProgress = true;
        try {
            let res = await this._validationController.validate();
            if (!res.valid) {
                // TODO inline validation is not properly working, likely due to the dropdown css, so we are throwing the validation here manually
                this._toastrService.error({
                    title: `Missing Data`,
                    message: `Please name the new filter to save.`
                });
                return;
            }
            let result = await this._userService.saveUserFilter(this.source, {
                name: this.updatedName,
                filters: this.searchFilters
            });
            let newOption = {
                name: this.updatedName,
                id: result.id
            };

            let existingFilter = this.savedFilters.find((f) => f.id == result.id);
            if (!existingFilter) {
                this.savedFilters.push(newOption);
            } else if (this.hasUpdatedName) {
                existingFilter.name = this.updatedName;
            }
            this.hasUpdatedName = false;
            this.selectedFilter = newOption;
            this._toastrService.success({
                title: `Success`,
                message: `The Filter set has been successfully Saved.`
            });
        } catch (err) {
            console.error(err);
        } finally {
            this.saveInProgress = false;
        }
    }

    public async deleteFilter(id: string, index: number) {
        this.saveInProgress = true;
        try {
            await this._userService.deleteUserFilter(id);
            this.savedFilters.splice(index, 1);
            if (this.selectedFilter?.id === id) {
                this.selectedFilter = null;
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.saveInProgress = false;
        }
    }

    public detached() {
        this._subscriptions?.forEach((f) => f.dispose());
        this.dropdownIconRef.removeEventListener("click", this._showDropdownListener);
        document?.removeEventListener("click", this._hideDropdownListener);
    }
}
